<template>
  <div class="progress-container">
    <div class="progress-header">
      <div class="progress-title">
        <h4>Progression de la tournée</h4>
      </div>
      <div class="progress-badge" :class="badgeClass">{{ statusText }}</div>
    </div>
    
    <div class="progress-stats">
      <div class="progress-count">
        <span class="completed">{{ completedCount }}</span>
        <span class="separator">/</span>
        <span class="total">{{ totalCount }}</span>
        <span class="label">livraisons</span>
      </div>
      <div class="progress-percentage-container">
        <div class="progress-percentage">{{ Math.round(percentage) }}%</div>
      </div>
    </div>
    
    <div class="progress-bar-container">
      <div class="markers">
        <div class="marker" v-for="n in 5" :key="n" :style="{ left: `${(n - 1) * 25}%` }">
          <div class="marker-line"></div>
          <div class="marker-label">{{ (n - 1) * 25 }}%</div>
        </div>
      </div>
      <div class="progress-bar" :style="{ width: `${percentage}%` }">
        <div class="progress-glow"></div>
      </div>
    </div>

    <!-- <div class="detail-stats">
      <div class="stat-item">
        <div class="stat-label">Livraisons standard</div>
        <div class="stat-value">{{ regularDeliveredCount }}/{{ regularDeliveriesCount }}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">Livraisons magasin</div>
        <div class="stat-value">{{ shopDeliveredCount }}/{{ shopDeliveriesCount }}</div>
      </div>
    </div> -->
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useTourneeData } from '../composables/useTourneeData';

// Utiliser le composable useTourneeData au lieu des props
const { tourneeData, combinedDeliveries } = useTourneeData();

// Compter les livraisons régulières terminées
const regularDeliveredCount = computed(() => {
  if (!tourneeData.value || !Array.isArray(tourneeData.value.deliveries)) return 0;
  return tourneeData.value.deliveries.filter(d => d.delivered === true).length;
});

// Compter les livraisons shop terminées
const shopDeliveredCount = computed(() => {
  if (!tourneeData.value || !Array.isArray(tourneeData.value.shop_deliveries)) return 0;
  return tourneeData.value.shop_deliveries.filter(d => d.status === 'delivered').length;
});

// Nombre total de livraisons terminées
const completedCount = computed(() => 
  regularDeliveredCount.value + shopDeliveredCount.value
);

// Nombre de livraisons régulières
const regularDeliveriesCount = computed(() => {
  if (!tourneeData.value || !Array.isArray(tourneeData.value.deliveries)) return 0;
  return tourneeData.value.deliveries.length;
});

// Nombre de livraisons shop
const shopDeliveriesCount = computed(() => {
  if (!tourneeData.value || !Array.isArray(tourneeData.value.shop_deliveries)) return 0;
  return tourneeData.value.shop_deliveries.length;
});

// Nombre total de livraisons
const totalCount = computed(() => 
  regularDeliveriesCount.value + shopDeliveriesCount.value
);

// Pourcentage de progression
const percentage = computed(() => {
  if (totalCount.value === 0) return 0;
  return (completedCount.value / totalCount.value) * 100;
});

// Texte de statut basé sur le pourcentage
const statusText = computed(() => {
  if (percentage.value === 100) return 'Terminé';
  if (percentage.value >= 75) return 'Presque fini';
  if (percentage.value >= 50) return 'Bien avancé';
  if (percentage.value >= 25) return 'En cours';
  return 'Démarrage';
});

// Classe CSS du badge basée sur le pourcentage
const badgeClass = computed(() => {
  if (percentage.value === 100) return 'completed';
  if (percentage.value >= 75) return 'almost';
  if (percentage.value >= 50) return 'halfway';
  if (percentage.value >= 25) return 'started';
  return 'beginning';
});
</script>

<style scoped>
.progress-container {
  width: 100%;
  margin: 0.25rem 0 1rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(145deg, #ffffff, #f5f7fa);
  border-radius: 0.75rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05),
              0 5px 10px -5px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(229, 231, 235, 0.5);
  transition: all 0.3s ease;
}

.progress-container:hover {
  box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.08),
              0 5px 15px -5px rgba(0, 0, 0, 0.04);
  transform: translateY(-1px);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.progress-title h4 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
}

.progress-subtitle {
  margin: 0.1rem 0 0;
  font-size: 0.7rem;
  color: #6b7280;
}

.progress-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.25rem 0.6rem;
  border-radius: 9999px;
  letter-spacing: 0.025em;
  text-transform: uppercase;
}

.progress-badge.beginning {
  background-color: rgba(209, 213, 219, 0.2);
  color: #6b7280;
}

.progress-badge.started {
  background-color: rgba(147, 197, 253, 0.2);
  color: #2563eb;
}

.progress-badge.halfway {
  background-color: rgba(52, 211, 153, 0.2);
  color: #059669;
}

.progress-badge.almost {
  background-color: rgba(249, 168, 212, 0.2);
  color: #db2777;
}

.progress-badge.completed {
  background-color: rgba(52, 211, 153, 0.2);
  color: #059669;
}

.progress-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.progress-count {
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 1rem;
}

.progress-count .completed {
  color: var(--primary-color, #3B82F6);
  font-weight: 700;
  font-size: 1.25rem;
}

.progress-count .separator {
  color: #9ca3af;
}

.progress-count .total {
  color: #4b5563;
  font-size: 1.25rem;
}

.progress-count .label {
  margin-left: 0.4rem;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 400;
}

.progress-percentage-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.progress-percentage {
  font-weight: 700;
  color: var(--primary-color, #3B82F6);
  background-color: rgba(59, 130, 246, 0.1);
  padding: 0.25rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.95rem;
}

.progress-bar-container {
  width: 100%;
  height: 0.6rem;
  background-color: #f3f4f6;
  border-radius: 9999px;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
  position: relative;
  margin-bottom: 0.75rem;
}

.markers {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.marker {
  position: absolute;
  height: 100%;
}

.marker-line {
  width: 1px;
  height: 60%;
  background-color: rgba(156, 163, 175, 0.3);
  margin: 0 auto;
}

.marker-label {
  position: absolute;
  bottom: -1.25rem;
  transform: translateX(-50%);
  font-size: 0.65rem;
  color: #9ca3af;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #3B82F6, #6366f1);
  border-radius: 9999px;
  transition: width 1.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(59, 130, 246, 0.4);
  z-index: 2;
}

.progress-bar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, 
    rgba(255, 255, 255, 0) 0%, 
    rgba(255, 255, 255, 0.5) 50%, 
    rgba(255, 255, 255, 0) 100%);
  width: 50%;
  animation: shimmer 2s infinite;
  transform: skewX(-20deg);
}

.progress-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 70%);
  animation: pulse 2s infinite;
}

.detail-stats {
  display: flex;
  justify-content: space-between;
  margin-top: 1.75rem;
  gap: 1rem;
}

.stat-item {
  flex: 1;
  padding: 0.75rem 1rem;
  background-color: #f9fafb;
  border-radius: 0.75rem;
  border: 1px solid rgba(229, 231, 235, 0.7);
  transition: all 0.2s ease;
}

.stat-item:hover {
  background-color: #ffffff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 
              0 2px 4px -1px rgba(0, 0, 0, 0.03);
}

.stat-label {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
}

@keyframes shimmer {
  0% {
    transform: translateX(-150%) skewX(-20deg);
  }
  100% {
    transform: translateX(250%) skewX(-20deg);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.2;
  }
  50% {
    opacity: 0.7;
  }
}

@media (max-width: 640px) {
  .progress-container {
    padding: 0.5rem 0.75rem;
    margin: 0.1rem 0 0.75rem;
    border-radius: 0.6rem;
  }

  .progress-header {
    margin-bottom: 0.4rem;
  }

  .progress-title h4 {
    font-size: 0.9rem;
  }

  .progress-subtitle {
    font-size: 0.65rem;
    margin: 0.05rem 0 0;
  }

  .progress-badge {
    font-size: 0.6rem;
    padding: 0.15rem 0.4rem;
  }

  .progress-stats {
    margin-bottom: 0.4rem;
  }

  .progress-count {
    font-size: 0.85rem;
  }

  .progress-count .completed,
  .progress-count .total {
    font-size: 1rem;
  }

  .progress-count .label {
    font-size: 0.75rem;
    margin-left: 0.3rem;
  }

  .progress-percentage {
    font-size: 0.85rem;
    padding: 0.2rem 0.5rem;
  }

  .progress-bar-container {
    height: 0.5rem;
    margin-bottom: 0.5rem;
  }
  
  .marker-label {
    bottom: -1.1rem;
    font-size: 0.6rem;
  }
}
</style>